import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Smartphone } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import StoreCard from "../components/StoreCard";
import ServiceGrid from "../components/ServiceGrid";
import { STORES } from "../data/siteData";

const HERO_LINES = ["IL TUO SISAL STORE,", "SEMPRE PIÙ VICINO."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[92svh] items-center overflow-hidden bg-ink">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero.jpg"
          alt="Interno di un Sisal Store moderno"
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/20" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950/70 to-transparent" />

      <div className="container-x relative z-10 py-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="overline-tag !text-emerald-300"
        >
          Tre sedi sul territorio — Veneto
        </motion.p>

        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg"
        >
          Tre sedi sul territorio. Scopri i nostri servizi, trova il punto vendita più
          comodo per te oppure accedi alla sezione dedicata al conto online.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.75 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row"
        >
          <Link to="/#sedi" data-testid="hero-cta-find-store" className="btn btn-ghost-light btn-lg">
            <MapPin className="h-5 w-5" />
            TROVA IL SISAL STORE PIÙ VICINO
          </Link>
          <Link to="/conto-online" data-testid="hero-cta-online-account" className="btn btn-primary btn-lg">
            <Smartphone className="h-5 w-5" />
            APRI IL TUO CONTO ONLINE
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-12 flex flex-wrap items-center gap-2.5"
        >
          {STORES.map((s) => (
            <Link
              key={s.id}
              to={s.slug}
              data-testid={`hero-chip-${s.id}`}
              className="rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              {s.shortName}
            </Link>
          ))}
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 text-white/70 md:block"
      >
        <ArrowDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
}

function ChapterHeading({ index, overline, title, subtitle, testid }) {
  return (
    <Reveal>
      <p className="overline-tag">
        {index} — {overline}
      </p>
      <h2
        data-testid={testid}
        className="mt-3 max-w-3xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl"
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-500 sm:text-lg">{subtitle}</p>
      )}
    </Reveal>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Sisal Store Mestre, Padova e Sant'Angelo di Piove di Sacco | Trova il punto vendita più vicino"
        description="Tre Sisal Store sul territorio: Mestre, Padova e Sant'Angelo di Piove di Sacco. Scopri servizi, orari, contatti e indicazioni stradali, oppure apri il tuo conto gioco online."
      />
      <Hero />
      <Marquee />

      <section id="sedi" data-testid="sedi-section" className="scroll-mt-24 py-20 sm:py-28">
        <div className="container-x">
          <ChapterHeading
            index="01"
            overline="Le nostre sedi"
            title="Trova il Sisal Store più vicino a te"
            subtitle="Scegli la sede e scopri tutti i servizi disponibili."
            testid="sedi-title"
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STORES.map((s, i) => (
              <StoreCard key={s.id} store={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section id="servizi" data-testid="servizi-section" className="scroll-mt-24 bg-slate-50 py-20 sm:py-28">
        <div className="container-x">
          <ChapterHeading
            index="02"
            overline="I nostri servizi"
            title="Tutto quello che cerchi, in un unico posto"
            subtitle="Dai un'occhiata ai servizi disponibili nei nostri punti vendita."
            testid="servizi-title"
          />
          <div className="mt-12">
            <ServiceGrid />
          </div>
        </div>
      </section>

      <section id="conto" data-testid="conto-section" className="scroll-mt-24 border-y border-emerald-100 bg-gradient-to-br from-emerald-50 to-green-100/60 py-20 sm:py-28">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="overline-tag">03 — Conto online</p>
              <h2
                data-testid="conto-section-title"
                className="mt-3 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl"
              >
                Vuoi aprire il tuo conto gioco online?
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Accedi alla sezione dedicata e segui i passaggi per effettuare la registrazione.
              </p>
              <div className="mt-8">
                <Link to="/conto-online" data-testid="conto-section-btn" className="btn btn-primary btn-lg">
                  <Smartphone className="h-5 w-5" />
                  APRI IL TUO CONTO ONLINE
                </Link>
              </div>
              <p className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <span className="flex h-7 w-7 items-center justify-center rounded-md border-2 border-slate-400 font-display text-[10px] font-bold text-slate-600">
                  18+
                </span>
                Servizio riservato ai maggiorenni
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-[2rem] shadow-[0_24px_60px_-20px_rgba(0,135,68,0.35)]">
              <img
                src="/images/conto-online.jpg"
                alt="Apri il tuo conto gioco Sisal e ricevi il bonus di benvenuto"
                loading="lazy"
                className="w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section id="assistenza" data-testid="assistenza-section" className="scroll-mt-24 py-20 sm:py-28">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-[2rem]">
              <img
                src="/images/assistenza.jpg"
                alt="Il nostro staff a disposizione dei clienti"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <p className="overline-tag">04 — Assistenza</p>
              <h2
                data-testid="assistenza-title"
                className="mt-3 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl"
              >
                Siamo qui per aiutarti
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg">
                Nei nostri Sisal Store trovi personale disponibile per fornirti informazioni sui
                servizi disponibili nei nostri punti vendita.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/#sedi" data-testid="assistenza-find-store-btn" className="btn btn-primary btn-lg">
                  <MapPin className="h-5 w-5" />
                  TROVA IL NEGOZIO
                </Link>
                <Link to="/contatti" data-testid="assistenza-contact-btn" className="btn btn-outline btn-lg">
                  CONTATTACI
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
